import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CriticalActivityList } from "../../components/modules/AuthActivity/CriticalActivityList/CriticalActivityList";
import { RealTimeComponent } from "./SSE";
import "./AuthActivityPage.css";
import Pagination from "../../components/common/Pagination/Pagination";
import { SearchBox } from "../../components/common";

// Redux actions
import {
  fetchCriticalActivities,
  setActiveTab,
  setCurrentPage,
  setCriticalSearchQuery,
} from "../../store/slices/authActivitySlice";

const AuthActivityPage = () => {
  const dispatch = useDispatch();

  // Redux selectors
  const {
    criticalActivities,
    activeTab,
    currentPage,
    pageSize,
  } = useSelector((state) => state.authActivity);

  const isCriticalTab = activeTab === "critical";
  const isNormalTab = activeTab === "normal";

  const { searchQuery } = criticalActivities;

  const handleRefresh = useCallback(() => {
    if (isCriticalTab) {
      dispatch(fetchCriticalActivities({ page: currentPage, limit: pageSize, search: searchQuery }));
    }
  }, [dispatch, isCriticalTab, currentPage, pageSize, searchQuery]);

  useEffect(() => {
    if (isCriticalTab) {
      dispatch(fetchCriticalActivities({ 
        page: currentPage, 
        limit: pageSize, 
        search: criticalActivities.searchQuery || undefined 
      }));
    }
  }, [activeTab, currentPage, pageSize, dispatch, isCriticalTab, criticalActivities.searchQuery]);

  const handleSearchChange = useCallback((value) => {
    if (isCriticalTab) {
      dispatch(setCriticalSearchQuery(value));
    }
  }, [dispatch, isCriticalTab]);

  const handleTabChange = useCallback((tab) => {
    dispatch(setActiveTab(tab));
    dispatch(setCurrentPage(1));
  }, [dispatch]);

  const handlePageChange = useCallback((newPage) => {
    dispatch(setCurrentPage(newPage));
  }, [dispatch]);

  const { displayData, paginationInfo, isLoading } = useMemo(() => {
    if (isCriticalTab) {
      return {
        displayData: criticalActivities.data || [],
        isLoading: criticalActivities.loading,
        paginationInfo: {
          page: currentPage,
          totalPages: criticalActivities.pagination?.total_pages || 0,
          totalCount: criticalActivities.pagination?.total_records || 0,
          pageSize: pageSize,
        }
      };
    }

    return { displayData: [], paginationInfo: {}, isLoading: false };
  }, [isCriticalTab, criticalActivities, currentPage, pageSize]);

  return (
    <div className="auth-activity-page">
      <div className="auth-activity-page__header">
        <div>
          <h1 className="auth-activity-page__title">Authentication Activity</h1>
          <p className="auth-activity-page__subtitle">
            Real-time feed of authentication events and risk flags
          </p>
        </div>
        <div className="auth-activity-page__actions">
          {isCriticalTab && (
            <SearchBox 
              value={criticalActivities.searchQuery}
              onChange={handleSearchChange}
              placeholder='Search Activities...'
              // className="activity-search"
            />
          )}
          
          {!isNormalTab && (
            <button className="refresh-button" onClick={handleRefresh} disabled={isLoading}>
              {isLoading ? "Refreshing..." : "↻ Refresh"}
            </button>
          )}
        </div>
      </div>

      <div className="auth-activity-page__tabs-container">
        <div className="auth-activity-page__tabs">
          <button
            className={`auth-activity-page__tab-item ${isNormalTab ? "active" : ""}`}
            onClick={() => handleTabChange("normal")}
          >
            Normal Activities
          </button>
          <button
            className={`auth-activity-page__tab-item ${isCriticalTab ? "active" : ""}`}
            onClick={() => handleTabChange("critical")}
          >
            Critical Activities
          </button>
        </div>
      </div>

      <div className="auth-activity-page__content">
        <div className="auth-activity-page__list">
          {isNormalTab ? (
            <RealTimeComponent />
          ) : (
            <>
              {isLoading ? (
                <div className="loader-container">
                  <div className="modern-spinner">
                    <div className="spinner-ring"></div>
                    <div className="spinner-ring"></div>
                    <div className="spinner-ring"></div>
                  </div>
                  <div className="loader-content">
                    <h3>Monitoring {activeTab} events...</h3>
                    <p>Fetching latest security logs from server</p>
                  </div>
                </div>
              ) : displayData.length === 0 ? (
                <div className="no-data-container">
                  <h3>No data available</h3>
                  <p>We couldn't find any {activeTab} activities</p>
                </div>
              ) : (
                <>
                  <CriticalActivityList 
                    events={displayData} 
                    onRefresh={handleRefresh}
                  />
                  {paginationInfo.totalPages > 1 && (
                    <Pagination
                      currentPage={paginationInfo.page}
                      totalPages={paginationInfo.totalPages}
                      totalItems={paginationInfo.totalCount}
                      itemsPerPage={paginationInfo.pageSize}
                      onPageChange={handlePageChange}
                    />
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthActivityPage;